/**
 * WalletEditModal — create/edit portfela. Nazwa + kolor (12-paleta).
 * Portfel typu 'shared' jest niezniszczalny (Wspólny rodziny).
 */
import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Icons } from '../Icons';
import { type Wallet } from '../../features/wallets/walletsStore';
import { useAuthStore } from '../../features/auth/authStore';
import { createWallet, updateWallet, deleteWallet } from '../../features/wallets/api';
import { haptic } from '../../lib/utils';
import { showToast } from '../ui/Toast';

const COLORS = ['#E53935', '#D81B60', '#8E24AA', '#5E35B1', '#1E88E5', '#00897B', '#43A047', '#F4511E', '#FB8C00', '#F6BF26', '#039BE5', '#00ACC1'];

interface Props {
  wallet: Wallet | null;
  onClose: () => void;
}

export function WalletEditModal({ wallet, onClose }: Props) {
  const family = useAuthStore((s) => s.family);
  const user = useAuthStore((s) => s.user);

  const isEdit = !!wallet;
  const isShared = wallet?.type === 'shared';
  const [name, setName] = useState(wallet?.name ?? '');
  const [color, setColor] = useState(wallet?.color ?? COLORS[0]!);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Podaj nazwę portfela');
      return;
    }
    if (!family || !user) return;
    setSubmitting(true);
    try {
      if (isEdit && wallet) {
        await updateWallet(wallet.id, { name: name.trim(), color });
        showToast('Zaktualizowano portfel', 'success');
      } else {
        await createWallet({
          familyId: family.id,
          ownerId: user.uid,
          name: name.trim(),
          color,
          type: 'personal',
        });
        showToast('Portfel utworzony', 'success');
      }
      haptic('success');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się zapisać');
      haptic('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!wallet || isShared) return;
    if (!window.confirm(`Usunąć portfel "${wallet.name}"? Transakcje pozostają, ale tracą przypisanie.`)) return;
    setSubmitting(true);
    try {
      await deleteWallet(wallet.id);
      showToast('Portfel usunięty', 'info');
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nie udało się usunąć');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      size="md"
      icon={
        <div className="w-16 h-16 rounded-full ring-8 ring-sky-500/20 flex items-center justify-center" style={{ backgroundColor: `${color}22`, color }}>
          <Icons.Wallet size={28} strokeWidth={2.25} />
        </div>
      }
      title={isEdit ? 'Edycja portfela' : 'Nowy portfel'}
      subtitle={isShared ? 'Portfel Wspólny rodziny' : undefined}
      footer={
        <div className="flex gap-2">
          {isEdit && !isShared && (
            <button type="button" onClick={handleDelete} disabled={submitting} className="px-3 py-3 rounded-xl border border-rose-200 dark:border-rose-500/30 text-rose-600 dark:text-rose-400 text-sm font-semibold hover:bg-rose-50 dark:hover:bg-rose-500/10 transition disabled:opacity-50">
              <Icons.Trash size={16} />
            </button>
          )}
          <button type="button" onClick={onClose} disabled={submitting} className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition disabled:opacity-50">Anuluj</button>
          <button type="button" onClick={handleSave} disabled={submitting} className="flex-[1.5] px-4 py-3 rounded-xl bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold shadow-card transition disabled:opacity-50">{submitting ? 'Zapisuję...' : isEdit ? 'Zapisz' : 'Utwórz'}</button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Nazwa</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="np. Damian"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Kolor</label>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`Kolor ${c}`}
                aria-pressed={c === color}
                className={`w-10 h-10 rounded-full transition ${c === color ? 'ring-4 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 ring-sky-500' : ''}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
        {error && (
          <div className="px-3 py-2.5 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 text-sm text-rose-700 dark:text-rose-300">{error}</div>
        )}
      </div>
    </Modal>
  );
}
